/**
 * components/landing/pricing/WlCheckoutModal.jsx — Phase 8 · 07/2026
 * =============================================================
 * Marketing-site White-Label checkout modal.  Opened by the
 * "Launch Starter / Professional / Enterprise" buttons on the
 * kalkiai.trade /pricing page.
 *
 * Contract:
 *   • Collects full name, work email, (optional) company.
 *   • Real-time (debounced) coupon validation via
 *     POST /api/wl/coupons/validate-public.
 *   • Shows Original / Discount / Final pricing preview.
 *   • Submits to POST /api/wl/public-checkout, then redirects to
 *     the Stripe Checkout URL returned in the response.
 *   • No auth required — the buyer creates their account at
 *     Stripe checkout completion (webhook auto-provisions a
 *     tenant + wl_admin user and emails credentials).
 */

import React, { useEffect, useMemo, useRef, useState } from 'react'
import { X, Loader2, Check, AlertCircle, Tag, Lock, ArrowRight } from 'lucide-react'
import { toast } from 'sonner'

const COUPON_ERRORS = {
  code_required:          'Enter a discount code.',
  not_found:              'This code does not exist.',
  inactive:               'This code is currently disabled.',
  not_started:            'This code is not active yet.',
  expired:                'This code has expired.',
  limit_reached:          'This code has reached its usage limit.',
  customer_limit_reached: 'You have already used this code.',
  plan_not_applicable:    'This code does not apply to the selected plan.',
}

const fmtUsd = (cents) => {
  const n = Math.round(Number(cents || 0) / 100)
  return `$${n.toLocaleString('en-US')}`
}

export default function WlCheckoutModal({ open, plan, onClose }) {
  const [name, setName]       = useState('')
  const [email, setEmail]     = useState('')
  const [company, setCompany] = useState('')

  const [couponInput, setCouponInput] = useState('')
  const [coupon, setCoupon]           = useState(null)   // valid response
  const [couponError, setCouponError] = useState(null)
  const [couponBusy, setCouponBusy]   = useState(false)

  const [submitting, setSubmitting] = useState(false)
  const [errors, setErrors]         = useState({})
  const [serverError, setServerError] = useState(null)

  // Reset all state whenever the modal opens for a new plan
  useEffect(() => {
    if (open) {
      setCouponInput(''); setCoupon(null); setCouponError(null)
      setServerError(null); setErrors({})
    }
  }, [open, plan?.planId])

  const debounceRef = useRef(null)

  // Debounced live coupon validation
  useEffect(() => {
    if (!open) return
    if (debounceRef.current) clearTimeout(debounceRef.current)
    const code = couponInput.trim()
    if (!code) {
      setCoupon(null); setCouponError(null); return
    }
    setCouponBusy(true)
    debounceRef.current = setTimeout(async () => {
      try {
        const r = await fetch('/api/wl/coupons/validate-public', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ code, plan: plan?.planId }),
        })
        const j = await r.json()
        if (j?.valid) { setCoupon(j); setCouponError(null) }
        else          { setCoupon(null); setCouponError(COUPON_ERRORS[j?.reason] || 'Invalid discount code.') }
      } catch {
        setCoupon(null); setCouponError('Could not validate code.')
      } finally { setCouponBusy(false) }
    }, 500)
    return () => { if (debounceRef.current) clearTimeout(debounceRef.current) }
  }, [couponInput, plan?.planId, open])

  // Pricing preview – prefer coupon.pricing[plan] when a coupon is valid
  const pricing = useMemo(() => {
    const original = plan?.amount_cents ?? 0
    const pv = coupon?.pricing?.[plan?.planId]
    if (pv) {
      return {
        original_cents: pv.original_cents ?? original,
        discount_cents: pv.discount_cents ?? 0,
        final_cents:    pv.final_cents ?? original,
      }
    }
    return { original_cents: original, discount_cents: 0, final_cents: original }
  }, [plan, coupon])

  const validate = () => {
    const e = {}
    if (!name.trim())  e.name  = 'Full name is required.'
    if (!email.trim()) e.email = 'Work email is required.'
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim())) e.email = 'Enter a valid email.'
    if (!company.trim()) e.company = 'Company is required.'
    setErrors(e)
    return Object.keys(e).length === 0
  }

  const submit = async () => {
    setServerError(null)
    if (!validate()) return
    setSubmitting(true)
    try {
      const r = await fetch('/api/wl/public-checkout', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          plan: plan?.planId,
          email: email.trim(),
          name: name.trim(),
          company: company.trim(),
          coupon_code: coupon?.valid ? coupon.coupon.code : '',
          origin_url: window.location.origin,
        }),
      })
      const j = await r.json()
      if (!r.ok || !j?.url) {
        const detail = j?.detail || j?.reason || j?.error || 'Checkout failed. Try again.'
        setServerError(detail)
        toast.error(detail)
        return
      }
      // Persist the buyer email so the success page can look up the session
      try {
        window.localStorage.setItem('wl_signup_last_session', j.session_id)
        window.localStorage.setItem('wl_signup_last_email', email.trim())
      } catch { /* localStorage may be blocked in some browsers */ }
      window.location.assign(j.url)
    } catch (e) {
      const msg = e?.message || 'Network error — try again.'
      setServerError(msg); toast.error(msg)
    } finally { setSubmitting(false) }
  }

  if (!open || !plan) return null

  const applied = coupon?.valid && pricing.discount_cents > 0

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center bg-black/85 backdrop-blur-md p-4 overflow-y-auto"
      role="dialog"
      data-testid="checkout-modal"
      aria-modal="true"
      onClick={onClose}
    >
      <div
        className="relative w-full max-w-lg my-8 rounded-3xl border border-white/10 bg-[#0b0b0f] shadow-[0_40px_120px_-20px_rgba(255,193,7,0.35)]"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Close */}
        <button
          onClick={onClose}
          className="absolute top-4 right-4 z-10 h-8 w-8 rounded-full border border-white/10 bg-white/[0.04] text-zinc-400 hover:text-white hover:bg-white/10 flex items-center justify-center transition"
          aria-label="Close checkout"
        >
          <X className="h-4 w-4" />
        </button>

        {/* Header */}
        <div className="px-7 pt-8 pb-5 border-b border-white/5">
          <div className="text-[11px] font-mono uppercase tracking-widest text-brand-gold mb-2">Secure Checkout</div>
          <div className="text-xl md:text-2xl font-semibold text-white leading-tight">{plan.label}</div>
          <div className="text-[13px] text-zinc-400 mt-1">{plan.duration} · billed via Stripe · cancel any time</div>
        </div>

        {/* Body */}
        <div className="px-7 py-6 space-y-5">
          {/* Personal fields */}
          <div className="grid gap-3 md:grid-cols-2">
            <Field
              label="Full name" value={name} onChange={setName}
              placeholder="Jane Doe" error={errors.name} required
            />
            <Field
              label="Work email" value={email} onChange={setEmail}
              placeholder="you@company.com" type="email" error={errors.email} required
            />
          </div>
          <Field
            label="Company" value={company} onChange={setCompany}
            placeholder="Acme Trading Ltd." error={errors.company} required
          />

          {/* Discount code */}
          <div className="flex flex-col gap-1.5">
            <label className="text-[11px] uppercase tracking-widest text-zinc-500 font-semibold flex items-center gap-1.5">
              <Tag className="h-3 w-3" /> Discount Code
            </label>
            <div className="relative">
              <input
                value={couponInput}
                onChange={(e) => setCouponInput(e.target.value.toUpperCase())}
                placeholder="Enter code (optional)"
                className={`w-full rounded-lg border bg-white/[0.03] px-3 py-2.5 text-sm text-white placeholder:text-zinc-500 uppercase tracking-wider font-mono
                  ${applied ? 'border-emerald-500/50 focus:border-emerald-500/70 focus:ring-2 focus:ring-emerald-500/20'
                    : couponError ? 'border-red-500/50 focus:border-red-500/70 focus:ring-2 focus:ring-red-500/20'
                    : 'border-white/10 focus:border-brand-gold/50 focus:ring-2 focus:ring-brand-gold/20'}
                  focus:outline-none transition`}
                autoComplete="off"
              />
              <div className="absolute right-3 top-1/2 -translate-y-1/2 flex items-center">
                {couponBusy && <Loader2 className="h-4 w-4 text-zinc-500 animate-spin" />}
                {!couponBusy && applied && <Check className="h-4 w-4 text-emerald-400" />}
                {!couponBusy && couponError && <AlertCircle className="h-4 w-4 text-red-400" />}
              </div>
            </div>
            {couponError && (
              <div className="text-[12px] text-red-400 flex items-start gap-1.5">
                <AlertCircle className="h-3 w-3 mt-0.5 shrink-0" /><span>{couponError}</span>
              </div>
            )}
            {applied && (
              <div className="text-[12px] text-emerald-400 flex items-start gap-1.5">
                <Check className="h-3 w-3 mt-0.5 shrink-0" />
                <span>
                  <b>{coupon.coupon.name}</b> applied —
                  {coupon.coupon.discount_type === 'percent'
                    ? ` ${coupon.coupon.percent_off}% off`
                    : ` ${fmtUsd(coupon.coupon.amount_off_cents)} off`}
                </span>
              </div>
            )}
          </div>

          {/* Pricing summary */}
          <div className="rounded-2xl border border-white/10 bg-white/[0.02] backdrop-blur-xl p-4 space-y-2">
            <PriceRow
              label="Original price"
              value={fmtUsd(pricing.original_cents)}
              muted={applied}
              strike={applied}
            />
            {applied && (
              <>
                {coupon.coupon.discount_type === 'percent' && (
                  <PriceRow label={`Discount (${coupon.coupon.percent_off}% off)`} value={`− ${fmtUsd(pricing.discount_cents)}`} tone="emerald" />
                )}
                {coupon.coupon.discount_type !== 'percent' && (
                  <PriceRow label="Discount" value={`− ${fmtUsd(pricing.discount_cents)}`} tone="emerald" />
                )}
              </>
            )}
            <div className="border-t border-white/5 pt-2 mt-1">
              <PriceRow
                label="You pay today"
                value={fmtUsd(pricing.final_cents)}
                emphasise
              />
              <div className="text-[11px] text-zinc-500 mt-1">Billed once for {plan.duration.toLowerCase()}. Auto-renews at the same rate unless cancelled.</div>
            </div>
          </div>

          {serverError && (
            <div className="rounded-lg border border-red-500/30 bg-red-500/10 px-3 py-2.5 text-[12.5px] text-red-300 flex items-start gap-2">
              <AlertCircle className="h-3.5 w-3.5 mt-0.5 shrink-0" />
              <span>{serverError}</span>
            </div>
          )}

          {/* Submit */}
          <button
            onClick={submit}
            data-testid="checkout-form-submit-button"
            disabled={submitting}
            className="w-full inline-flex items-center justify-center gap-2 rounded-full bg-gradient-to-r from-brand-gold to-[#FFE17A] text-black font-semibold py-3.5 text-[15px] shadow-[0_10px_40px_-10px_rgba(255,193,7,0.55)] hover:shadow-[0_16px_50px_-8px_rgba(255,193,7,0.75)] transition-all disabled:opacity-60"
          >
            {submitting ? (
              <><Loader2 className="h-4 w-4 animate-spin" /> Redirecting…</>
            ) : (
              <><Lock className="h-4 w-4" /> Continue to Stripe · {fmtUsd(pricing.final_cents)} <ArrowRight className="h-4 w-4" /></>
            )}
          </button>

          <div className="text-[11px] text-zinc-500 text-center">
            Powered by Stripe · PCI-DSS Level 1 · Invoice + workspace credentials arrive by email within 30 seconds of payment.
          </div>
        </div>
      </div>
    </div>
  )
}

const Field = ({ label, value, onChange, type = 'text', placeholder = '', error = null, required = false }) => (
  <label className="flex flex-col gap-1">
    <span className="text-[11px] uppercase tracking-widest text-zinc-500 font-semibold">
      {label}{required && <span className="text-brand-gold">*</span>}
    </span>
    <input
      type={type}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      required={required}
      className={`rounded-lg border bg-white/[0.03] px-3 py-2.5 text-sm text-white placeholder:text-zinc-500 focus:outline-none focus:ring-2 transition
        ${error
          ? 'border-red-500/50 focus:border-red-500/70 focus:ring-red-500/20'
          : 'border-white/10 focus:border-brand-gold/50 focus:ring-brand-gold/20'}`}
    />
    {error && (
      <span className="text-[11.5px] text-red-400 flex items-center gap-1">
        <AlertCircle className="h-3 w-3" /> {error}
      </span>
    )}
  </label>
)

const PriceRow = ({ label, value, tone = 'default', emphasise = false, muted = false, strike = false }) => {
  const toneColor = tone === 'emerald' ? 'text-emerald-400' : (emphasise ? 'text-white' : 'text-zinc-300')
  return (
    <div className="flex items-center justify-between">
      <span className={`text-[13px] ${muted ? 'text-zinc-500' : emphasise ? 'text-white font-semibold' : 'text-zinc-400'}`}>
        {label}
      </span>
      <span className={`text-[15px] font-semibold ${toneColor} ${strike ? 'line-through' : ''} ${emphasise ? 'text-xl md:text-2xl' : ''}`}>
        {value}
      </span>
    </div>
  )
}
